/**
 * Discipline Breakdown Tab Component for RNCM Productivity
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Plus, Trash2 } from 'lucide-react';

export const DisciplineTab = ({
  disciplineSummaries,
  canEdit,
  onAddSummary,
  onDelete
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Discipline Breakdown
            </CardTitle>
            <CardDescription>Visit distribution by discipline with benchmarks</CardDescription>
          </div>
          {canEdit && (
            <Button size="sm" onClick={onAddSummary}>
              <Plus className="h-4 w-4 mr-1" /> Add Summary
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Benchmarks Section */}
        <div className="mb-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-3">Benchmarks</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">RNCMs</p>
              <p className="font-semibold">65% - 75%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">LPNs</p>
              <p className="font-semibold">5% - 10%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Wound RN</p>
              <p className="font-semibold">3% - 7%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">PRNs (Weekday)</p>
              <p className="font-semibold">15% - 25%</p>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>RNCMs</TableHead>
              <TableHead>LPNs</TableHead>
              <TableHead>Wound RN</TableHead>
              <TableHead>PRNs</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {disciplineSummaries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No discipline summaries yet. Add data to track.
                </TableCell>
              </TableRow>
            ) : (
              disciplineSummaries.map((s, idx) => (
                <TableRow key={s.id || idx}>
                  <TableCell className="font-medium">{s.period_start}</TableCell>
                  <TableCell>
                    {s.rncm_visits} <span className="text-muted-foreground">({s.rncm_percent}%)</span>
                  </TableCell>
                  <TableCell>
                    {s.lpn_visits} <span className="text-muted-foreground">({s.lpn_percent}%)</span>
                  </TableCell>
                  <TableCell>
                    {s.wound_rn_visits} <span className="text-muted-foreground">({s.wound_rn_percent}%)</span>
                  </TableCell>
                  <TableCell>
                    {s.prn_visits} <span className="text-muted-foreground">({s.prn_percent}%)</span>
                  </TableCell>
                  <TableCell className="font-semibold">{s.total_visits}</TableCell>
                  <TableCell className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDelete(s)}
                      data-testid={`delete-discipline-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default DisciplineTab;
